import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import React from "react";
import type { Exact, ExactArrayItems } from "@Helpers/types/exact";
import type { ProjectDataItem } from "@ServerTypes/projects/project.schema";
import type { SpaceDataItem } from "@ServerTypes/spaces/space.schema";
import type { ObjectIdString } from "@Helpers/schemas/common.schema";

/** ProjectData extends the backend ProjectDataItem with frontend-only state */
export interface ProjectData extends ProjectDataItem {
  isDataFetched: boolean;
  /**
   * Default space of the project (frontend convenience field). `_id` is the embedded
   * `projects.defaultSpace._id`, an `ObjectId` in `projects.collection.ts`, so it is branded
   * here rather than re-declared as a plain `string`.
   */
  defaultSpace?: { _id: ObjectIdString; name: string } | undefined;
}

/**
 * `Partial`, but every member may also be explicitly `undefined`. `Exact<Partial<T>, U>` maps each
 * member through `Exact<T[K] | undefined, …>` (reading an optional property always yields the
 * `undefined` arm), so its result cannot be assigned back into a plain `Partial<T>` under
 * `exactOptionalPropertyTypes`. Mirrors the same alias in `reducers/projectDashboard.ts`.
 */
type PartialOrUndefined<T> = { [K in keyof T]?: T[K] | undefined };

/**
 * Every member of the project record except `isDataFetched`, which the reducer defaults to `true`
 * when the payload omits it. (`PartialOrUndefined<ProjectData>` is what makes `isDataFetched`
 * optional here; the rest of its members are already covered by the `Omit`.)
 */
type SetProjectDataPayload = Omit<ProjectData, "isDataFetched"> & PartialOrUndefined<ProjectData>;

/** Breadcrumb item — text may be a string, translated string, or JSX element */
export interface BreadCrumbItem {
  text?: React.ReactNode | undefined;
  link?: string | undefined;
  showTooltip?: boolean | undefined;
  tooltipData?: string | undefined;
  template?: (() => React.ReactNode) | undefined;
}

const projectDataSlice = createSlice({
  name: "projectData",
  initialState: { isDataFetched: false } as ProjectData,
  reducers: {
    setProjectData: (_state: ProjectData, action: PayloadAction<SetProjectDataPayload>) => ({
      ...action.payload,
      isDataFetched: action.payload.isDataFetched ?? true,
    }),
  },
});

const allSpacesInProjectSlice = createSlice({
  name: "allSpacesInProject",
  initialState: [] as SpaceDataItem[],
  reducers: {
    setAllSpacesInProjects: (_state: SpaceDataItem[], action: PayloadAction<SpaceDataItem[]>) => action.payload,
  },
});

const spaceDataSlice = createSlice({
  name: "spaceData",
  initialState: {} as SpaceDataItem,
  reducers: {
    setSpaceData: (_state: SpaceDataItem, action: PayloadAction<SpaceDataItem>) => action.payload,
  },
});

const breadCrumbDataSlice = createSlice({
  name: "breadCrumbData",
  initialState: [] as BreadCrumbItem[],
  reducers: {
    setBreadCrumbData: (_state: BreadCrumbItem[], action: PayloadAction<BreadCrumbItem[]>) => action.payload,
  },
});

// Internal raw action creators (Redux Toolkit auto-generated, no excess-property
// checking on variable arguments).
const { setProjectData: _setProjectDataRaw } = projectDataSlice.actions;
const { setAllSpacesInProjects: _setAllSpacesInProjectsRaw } = allSpacesInProjectSlice.actions;
const { setSpaceData: _setSpaceDataRaw } = spaceDataSlice.actions;
const { setBreadCrumbData: _setBreadCrumbDataRaw } = breadCrumbDataSlice.actions;

/**
 * Object-payload action creators are wrapped with `Exact<>` so excess properties
 * are caught even when the payload is passed through a variable (Redux Toolkit's
 * auto-generated action creators lose excess-property checking once the literal
 * is assigned to a variable).
 *
 * `Exact<>` is applied to the PARAMETER (`T & Exact<…>`) rather than used as the constraint — the
 * same form `reducers/fileSync.ts` and `reducers/measurements.ts` document. Under
 * `exactOptionalPropertyTypes` type-fest's `ExactObject` homomorphic mapping re-emits every optional
 * member as `k?: V | undefined`, so a bare `T extends Exact<SetProjectDataPayload, T>` no longer
 * proves `T` is a `SetProjectDataPayload`. Constraining `T` to the payload type and intersecting
 * `Exact<>` into the parameter keeps both properties: the argument is still assignable to the raw
 * action creator, and excess keys are still mapped to `never` and rejected.
 */
export const setProjectData = <T extends SetProjectDataPayload>(payload: T & Exact<SetProjectDataPayload, T>) =>
  _setProjectDataRaw(payload);

/**
 * `ExactArrayItems<>` works for HOMOGENEOUS arrays where every element shares
 * the same field set (entity lists). It runs per-element `Exact<>` checking,
 * catching excess properties even when the array is built via a variable.
 */
export const setAllSpacesInProjects = <T extends readonly SpaceDataItem[]>(
  payload: T & ExactArrayItems<SpaceDataItem, T>,
) => _setAllSpacesInProjectsRaw([...payload]);

export const setSpaceData = <T extends Exact<SpaceDataItem, T>>(payload: T) => _setSpaceDataRaw(payload);

/**
 * `setBreadCrumbData` is intentionally NOT wrapped. Breadcrumb arrays are
 * HETEROGENEOUS — different entries omit different optional fields (some have
 * `link`, some have `template`, some only `text`). `ExactArrayItems<>` infers
 * the element type as a union of all sibling shapes and then forces every
 * element to match that union, which rejects legitimate use cases (e.g. a
 * `{ text: <JSX/> }` entry next to `{ text, link, showTooltip, tooltipData }`).
 * For breadcrumb excess-property safety, prefer the inline-array pattern at
 * the call site (fresh literal → TS enforces excess property checking), or
 * use `... satisfies BreadCrumbItem[]` if assigning to a variable.
 */
export const setBreadCrumbData = _setBreadCrumbDataRaw;

export const projectData = projectDataSlice.reducer;
export const allSpacesInProject = allSpacesInProjectSlice.reducer;
export const spaceData = spaceDataSlice.reducer;
export const breadCrumbData = breadCrumbDataSlice.reducer;

export default breadCrumbDataSlice.reducer;
